import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  Divider,
  Input,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Autocomplete,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useForm, Controller } from "react-hook-form";
import axios from "axios";
import { AppDispatch, RootState } from "src/store";
import {
  CompanyFormInputs,
  updateCompany,
  fetchCompany,
  CompanyUser,
} from "src/features/authSlice";
import { API_URL } from "src/config/api";
import { useTranslation } from "react-i18next";
import i18n from "src/i18n";
import { normalizeApiLanguage } from "src/utils/language";

const CompanyPage: React.FC = () => {
  const { t } = useTranslation();
  const currentLanguage = normalizeApiLanguage(i18n.resolvedLanguage || i18n.language);
  const dispatch = useDispatch<AppDispatch>();
  const { user, loading, token } = useSelector((state: RootState) => state.auth);

  // Type guard: This is a company page, so user should be CompanyUser
  const companyUser = user?.accountType === "company" ? (user as CompanyUser) : null;

  type SelectableItem = { key: string; text: string };

  // Company type, company size and sector options from selectables (key/text pairs)
  const [companyTypeOptions, setCompanyTypeOptions] = useState<SelectableItem[]>([]);
  const [companySizeOptions, setCompanySizeOptions] = useState<SelectableItem[]>([]);
  const [sectorOptions, setSectorOptions] = useState<SelectableItem[]>([]);

  const { control, handleSubmit, setValue, reset } = useForm<CompanyFormInputs>({
    defaultValues: {
      companyName: companyUser?.name || companyUser?.companyName || "",
      companyWebsite: companyUser?.website || companyUser?.companyWebsite || "",
      companyLegalName: companyUser?.legalName || companyUser?.companyLegalName || "",
      companyAddress: companyUser?.location?.address || companyUser?.companyAddress || "",
      taxNumber: companyUser?.taxNumber || "",
      taxOffice: companyUser?.taxOffice || "",
      companyCity: companyUser?.location?.city || companyUser?.companyCity || "",
      companyCountry: companyUser?.location?.country || companyUser?.companyCountry || "",
      companySector: companyUser?.sector || companyUser?.companySector || "",
      companyType: companyUser?.companyType || "",
      companySize: companyUser?.companySize || "",
      postalCode: companyUser?.postalCode || "",
      authorizedSignatoryName: companyUser?.authorizedSignatoryName || "",
      authorizedSignatoryIdCardSoftCopy: companyUser?.authorizedSignatorySoftcopyId || companyUser?.authorizedSignatoryIdCardSoftCopy || undefined,
      companyLogo: companyUser?.companyLogo || "",
    },
  });

  // Fetch company details on mount
  useEffect(() => {
    if (companyUser?.companyId) {
      dispatch(fetchCompany(companyUser.companyId));
    }
  }, [dispatch, companyUser?.companyId]);

  // Update form when companyUser data changes (after fetch from backend)
  useEffect(() => {
    if (companyUser) {
      reset({
        companyName: companyUser.name || companyUser.companyName || "",
        companyWebsite: companyUser.website || companyUser.companyWebsite || "",
        companyLegalName: companyUser.legalName || companyUser.companyLegalName || "",
        companyAddress: companyUser.location?.address || companyUser.companyAddress || "",
        taxNumber: companyUser.taxNumber || "",
        taxOffice: companyUser.taxOffice || "",
        companyCity: companyUser.location?.city || companyUser.companyCity || "",
        companyCountry: companyUser.location?.country || companyUser.companyCountry || "",
        companySector: companyUser.sector || companyUser.companySector || "",
        companyType: companyUser.companyType || "",
        companySize: companyUser.companySize || "",
        postalCode: companyUser.postalCode || "",
        authorizedSignatoryName: companyUser.authorizedSignatoryName || "",
        authorizedSignatoryIdCardSoftCopy: companyUser.authorizedSignatorySoftcopyId || companyUser.authorizedSignatoryIdCardSoftCopy || undefined,
        companyLogo: companyUser.companyLogo || "",
      });
    }
  }, [companyUser, reset]);

  // Fetch companyType options from selectables (key/text pairs)
  useEffect(() => {
    const fetchCompanyTypes = async () => {
      try {
        const response = await axios.post(
          `${API_URL}/fuzzy/fuzzyWithItemSearch`,
          { name: 'companyTypes', statement: '', for: 'company', language: currentLanguage },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const items = response.data.map((item: { key: string; text: string }) => ({
          key: item.key,
          text: item.text,
        }));
        setCompanyTypeOptions(items);
      } catch (error) {
        console.error('Failed to fetch companyTypes:', error);
        // Fallback to known identifiers
        setCompanyTypeOptions([
          { key: 'startup', text: t("settings.company.companyType.startUp") },
          { key: 'corporate', text: t("settings.company.companyType.corporate") },
          { key: 'nonprofit', text: t("settings.company.companyType.nonProfit") },
        ]);
      }
    };
    if (token) {
      fetchCompanyTypes();
    }
  }, [token, t, currentLanguage]);

  // Fetch companySize options from selectables (key/text pairs)
  useEffect(() => {
    const fetchCompanySizes = async () => {
      try {
        const response = await axios.post(
          `${API_URL}/fuzzy/fuzzyWithItemSearch`,
          { name: "companySizes", statement: "", for: "company", language: currentLanguage },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const items = response.data.map((item: { key: string; text: string }) => ({
          key: item.key,
          text: item.text,
        }));
        setCompanySizeOptions(items);
      } catch (error) {
        console.error("Failed to fetch companySizes:", error);
        setCompanySizeOptions([
          { key: "seed", text: t("settings.company.companySize.seed") },
          { key: "small", text: t("settings.company.companySize.small") },
          { key: "medium", text: t("settings.company.companySize.medium") },
          { key: "large", text: t("settings.company.companySize.large") },
          { key: "verylarge", text: t("settings.company.companySize.veryLarge") },
          { key: "massive", text: t("settings.company.companySize.massive") },
        ]);
      }
    };
    if (token) {
      fetchCompanySizes();
    }
  }, [token, t, currentLanguage]);

  // Search sectors with min 2 characters
  const [sectorSearchInput, setSectorSearchInput] = useState("");

  const searchSectors = async (searchText: string) => {
    if (searchText.length < 2) {
      setSectorOptions([]);
      return;
    }
    try {
      const response = await axios.post(
        `${API_URL}/fuzzy/fuzzyWithItemSearch`,
        { name: 'sectors', statement: searchText, for: 'company', language: currentLanguage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const items = response.data.map((item: { key: string; text: string }) => ({
        key: item.key,
        text: item.text,
      }));
      setSectorOptions(items);
    } catch (error) {
      console.error('Failed to fetch sectors:', error);
      setSectorOptions([]);
    }
  };

  const [editingField, setEditingField] = useState<
    keyof CompanyFormInputs | null
  >(null);

  const handleEditClick = (fieldValue: keyof CompanyFormInputs, backendField: string) => {
    setEditingField(fieldValue);
    // Get the current value from companyUser using backendField
    let currentValue = "";
    if (companyUser) {
      if (backendField.includes(".")) {
        const [parent, child] = backendField.split(".");
        const parentObj = (companyUser as any)[parent];
        currentValue = parentObj?.[child] || "";
      } else {
        currentValue = (companyUser as any)[backendField] || "";
      }
    }
    setValue(fieldValue, currentValue);
  };

  const normalizeSelectableValue = (
    value: string,
    options: SelectableItem[]
  ): string => {
    if (!value) return "";
    const byKey = options.find((option) => option.key === value);
    if (byKey) return byKey.key;
    const byText = options.find((option) => option.text === value);
    return byText ? byText.key : value;
  };

  const handleSaveClick = async (data: CompanyFormInputs) => {
    const companyId = companyUser?.companyId;
    if (!companyId) return;

    const normalizedData = {
      ...data,
      companyType: normalizeSelectableValue(data.companyType, companyTypeOptions),
      companySize: normalizeSelectableValue(data.companySize, companySizeOptions),
      companySector: normalizeSelectableValue(data.companySector, sectorOptions),
      postalCode: (data.postalCode || "").replace(/\D/g, ""),
    };

    const result = await dispatch(updateCompany({ id: companyId, ...normalizedData }));

    if (updateCompany.fulfilled.match(result)) {
      setEditingField(null);
    }
  };

  // Only show fields that exist in Backend CompanyUserDTO
  const availableFields = [
    { label: t("settings.company.fields.companyName"), value: "companyName" as const, backendField: "name" as const },
    { label: t("settings.company.fields.companyWebsite"), value: "companyWebsite" as const, backendField: "website" as const },
    { label: t("settings.company.fields.companyLegalName"), value: "companyLegalName" as const, backendField: "legalName" as const },
    { label: t("settings.company.fields.companyCountry"), value: "companyCountry" as const, backendField: "location.country" as const },
    { label: t("settings.company.fields.companyCity"), value: "companyCity" as const, backendField: "location.city" as const },
    { label: t("settings.company.fields.companyAddress"), value: "companyAddress" as const, backendField: "location.address" as const },
    { label: t("settings.company.fields.postalCode"), value: "postalCode" as const, backendField: "postalCode" as const },
    { label: t("settings.company.fields.companySector"), value: "companySector" as const, backendField: "sector" as const },
    { label: t("settings.company.fields.companyType"), value: "companyType" as const, backendField: "companyType" as const },
    { label: t("settings.company.fields.companySize"), value: "companySize" as const, backendField: "companySize" as const },
    { label: t("settings.company.fields.taxNumber"), value: "taxNumber" as const, backendField: "taxNumber" as const },
    { label: t("settings.company.fields.taxOffice"), value: "taxOffice" as const, backendField: "taxOffice" as const },
    {
      label: t("settings.company.fields.authorizedSignatoryName"),
      value: "authorizedSignatoryName" as const,
      backendField: "authorizedSignatoryName" as const,
    },
  ];

  // Helper function to get value from companyUser (handles both backend API format and CompanyUserDTO format)
  const getFieldValue = (backendField: string): string => {
    if (!companyUser) return "";

    // Handle nested properties (location.city, location.country, etc.)
    if (backendField.includes(".")) {
      const [parent, child] = backendField.split(".");
      const parentObj = (companyUser as any)[parent];
      return parentObj?.[child] || "";
    }

    return (companyUser as any)[backendField] || "";
  };

  const getOptionLabel = (options: SelectableItem[], value: string): string => {
    if (!value) return "";
    return options.find((option) => option.key === value)?.text || value;
  };

  return (
    <Box sx={{ margin: "auto", padding: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {t("settings.company.title")}
      </Typography>

      {loading && <Typography>{t("settings.company.loading")}</Typography>}
      <Divider sx={{ mb: 2 }} />
      {availableFields.map((field) => {
        const rawValue = getFieldValue(field.backendField);
        const displayValue =
          field.value === "companyType"
            ? getOptionLabel(companyTypeOptions, rawValue)
            : field.value === "companySize"
              ? getOptionLabel(companySizeOptions, rawValue)
            : field.value === "companySector"
              ? getOptionLabel(sectorOptions, rawValue)
              : rawValue;

        return (
        <Box key={field.value} sx={{ mb: 2 }}>
          {editingField === field.value ? (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Controller
                name={field.value}
                control={control}
                render={({ field: controllerField }) => (
                  field.value === "companyType" ? (
                    <FormControl fullWidth>
                      <InputLabel>{field.label}</InputLabel>
                      <Select
                        {...controllerField}
                        label={field.label}
                        variant="outlined"
                      >
                        {companyTypeOptions.map((option) => (
                          <MenuItem key={option.key} value={option.key}>
                            {option.text}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : field.value === "companySize" ? (
                    <FormControl fullWidth>
                      <InputLabel>{field.label}</InputLabel>
                      <Select
                        {...controllerField}
                        label={field.label}
                        variant="outlined"
                      >
                        {companySizeOptions.map((option) => (
                          <MenuItem key={option.key} value={option.key}>
                            {option.text}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : field.value === "companySector" ? (
                    <Autocomplete
                      fullWidth
                      options={sectorOptions}
                      getOptionLabel={(option) => option.text}
                      isOptionEqualToValue={(option, value) => option.key === value.key}
                      inputValue={sectorSearchInput}
                      onInputChange={(_, newInputValue) => {
                        setSectorSearchInput(newInputValue);
                        searchSectors(newInputValue);
                      }}
                      onChange={(_, newValue) => {
                        controllerField.onChange(newValue?.key || "");
                      }}
                      value={sectorOptions.find(opt => opt.key === controllerField.value) || null}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={field.label}
                          variant="outlined"
                          placeholder={t("settings.company.searchPlaceholder")}
                        />
                      )}
                    />
                  ) : field.value === "postalCode" ? (
                    <TextField
                      {...controllerField}
                      label={field.label}
                      variant="outlined"
                      fullWidth
                      inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                      onChange={(e) => {
                        controllerField.onChange(e.target.value.replace(/\D/g, ""));
                      }}
                    />
                  ) : (
                    <TextField
                      {...controllerField}
                      label={field.label}
                      variant="outlined"
                      fullWidth
                    />
                  )
                )}
              />
              <Button
                onClick={handleSubmit(handleSaveClick)}
                variant="contained"
                color="primary"
                sx={{ ml: 2 }}
              >
                {t("settings.company.save")}
              </Button>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="body1" sx={{ color: "gray" }}>
                {field.label}
              </Typography>
              <Typography variant="body1">
                {displayValue}
              </Typography>
              <IconButton
                edge="end"
                aria-label="edit"
                onClick={() => handleEditClick(field.value, field.backendField)}
              >
                <EditIcon color="primary" />
              </IconButton>
            </Box>
          )}
          <Divider sx={{ mt: 1 }} />
        </Box>
        );
      })}
      <Box sx={{ mb: 2, mt: 3 }}>
        <Typography variant="body1" sx={{ color: "gray", mb: 1 }}>
          {t("settings.company.companyLogo")}
        </Typography>
        {companyUser?.companyLogo && (
          <Box sx={{ mb: 2 }}>
            <img
              src={companyUser.companyLogo}
              alt={t("settings.company.companyLogo")}
              style={{ maxWidth: "200px", maxHeight: "200px" }}
            />
          </Box>
        )}
        <Controller
          name="companyLogo"
          control={control}
          render={({ field: { onChange } }) => (
            <Input
              type="file"
              inputProps={{ accept: "image/*" }}
              onChange={(e) => {
                const file = (e.target as HTMLInputElement).files?.[0] || null;
                onChange(file);
              }}
            />
          )}
        />
        <Button
          onClick={handleSubmit(handleSaveClick)}
          variant="contained"
          color="primary"
          sx={{ mt: 1 }}
        >
          {t("settings.company.save")}
        </Button>
      </Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="body1" sx={{ color: "gray" }}>
          {t("settings.company.authorizedId")}
        </Typography>
        {(() => {
          const idCardSrc =
            companyUser?.authorizedSignatorySoftcopyId ||
            companyUser?.authorizedSignatoryIdCardSoftCopy ||
            "";
          if (!idCardSrc) return null;
          const isDataUrl = idCardSrc.startsWith("data:");
          const isImage = isDataUrl ? idCardSrc.startsWith("data:image/") : true;

          return (
            <Box sx={{ mb: 2 }}>
              {isImage ? (
                <img
                  src={idCardSrc}
                  alt={t("settings.company.authorizedId")}
                  style={{ maxWidth: "200px", maxHeight: "200px" }}
                />
              ) : (
                <a href={idCardSrc} target="_blank" rel="noreferrer">
                  {t("settings.company.viewIdCard")}
                </a>
              )}
            </Box>
          );
        })()}
        <Controller
          name="authorizedSignatoryIdCardSoftCopy"
          control={control}
          render={({ field: { onChange } }) => (
            <Input
              type="file"
              onChange={(e) => {
                const file = (e.target as HTMLInputElement).files?.[0] || null;
                onChange(file);
              }}
            />
          )}
        />
        <Button
          onClick={handleSubmit(handleSaveClick)}
          variant="contained"
          color="primary"
          sx={{ mt: 1 }}
        >
          {t("settings.company.save")}
        </Button>
      </Box>
    </Box>
  );
};

export default CompanyPage;
